import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { LEVEL_COLORS } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogOut, Share2, Mail, Flame, Save, Link2, Copy, Trash2, Crown, Search, Shield } from "lucide-react";
import { toast } from "sonner";
import useSubscription from "../hooks/useSubscription";
import { ProLock } from "../components/ProGate";
import { PushToggleRow } from "../components/PushOptIn";
import InstallPrompt from "../components/InstallPrompt";
import FitbitConnect from "../components/FitbitConnect";
import TrainerProfileWidget from "../components/TrainerProfileWidget";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [gym, setGym] = useState(user?.gym_name || "");
  const [shares, setShares] = useState([]);
  const [creating, setCreating] = useState(false);
  const { sub, refresh: refreshSub } = useSubscription();
  const [unit, setUnit] = useState(user?.distance_unit || "km");
  const isAdmin = !!user?.is_admin;
  const [adminQuery, setAdminQuery] = useState("");
  const [adminResults, setAdminResults] = useState([]);
  const [scholarships, setScholarships] = useState([]);

  useEffect(() => { (async () => {
    const [s, sh] = await Promise.all([api.get("/me/stats"), api.get("/share/my").catch(() => ({ data: [] }))]);
    setStats(s.data); setShares(sh.data);
    if (isAdmin) {
      const sc = await api.get("/admin/scholarships");
      setScholarships(sc.data);
    }
  })(); }, [isAdmin]);

  useEffect(() => { setName(user?.name || ""); setGym(user?.gym_name || ""); setUnit(user?.distance_unit || "km"); }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    await api.put("/me", { name, gym_name: gym, distance_unit: unit });
    await refresh();
    toast.success("Profile updated");
  };

  const createShare = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/share/progress");
      const url = `${window.location.origin}/share/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Share link copied");
      const { data: list } = await api.get("/share/my");
      setShares(list);
    } catch (e) {
      toast.error(e.response?.status === 402 ? "Pro feature — upgrade to share with trainers" : "Failed");
    } finally { setCreating(false); }
  };

  const searchUsers = async () => {
    const { data } = await api.get(`/admin/users?q=${encodeURIComponent(adminQuery)}`);
    setAdminResults(data);
  };
  const toggleScholarship = async (uid, on) => {
    await api.post("/admin/scholarship", { user_id: uid, on });
    toast.success(on ? "Scholarship granted" : "Revoked");
    if (adminQuery) await searchUsers();
    const sc = await api.get("/admin/scholarships");
    setScholarships(sc.data);
  };

  const copy = (token) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Copied");
  };
  const revoke = async (token) => {
    await api.delete(`/share/${token}`);
    setShares((s) => s.filter((x) => x.share_token !== token));
    toast.success("Revoked");
  };

  const doLogout = async () => { await logout(); nav("/login"); };
  const shareApp = () => {
    const url = window.location.origin;
    if (navigator.share) navigator.share({ title: "GYM with GRIZZ", text: "Join me on GYM with GRIZZ!", url });
    else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  if (!user) return null;
  const lvlColor = stats ? LEVEL_COLORS[stats.level.name] : "#fff";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-5">Profile</h1>

      <div className="border border-neutral-800 p-5 mb-5 flex items-center gap-4">
        <div className="w-16 h-16 bg-blue-500 flex items-center justify-center font-display text-2xl text-white overflow-hidden">
          {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl uppercase">{user.name}</div>
          <div className="text-xs text-neutral-500">{user.email}</div>
          {user.gym_name && <div className="text-xs text-blue-500 mt-0.5">@ {user.gym_name}</div>}
        </div>
        <Flame className="w-6 h-6 text-blue-500" />
      </div>

      {stats && (
        <div className="border border-neutral-800 p-5 mb-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs tracking-widest uppercase text-neutral-500">Current Level</div>
              <div className="font-display text-3xl uppercase mt-1" style={{ color: lvlColor }}>{stats.level.name}</div>
            </div>
            <div className="text-right">
              <div className="text-xs tracking-widest uppercase text-neutral-500">Points</div>
              <div className="font-display text-3xl mt-1">{stats.points}</div>
            </div>
          </div>
          {stats.level.next && (
            <div className="mt-3 text-xs text-neutral-400">{stats.level.next.threshold - stats.points} pts to {stats.level.next.name}</div>
          )}
        </div>
      )}

      {sub && (
        <div data-testid="subscription-card" className={`border ${sub.is_pro ? "border-blue-500" : "border-neutral-800"} p-5 mb-5`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Crown className={`w-4 h-4 ${sub.is_pro ? "text-blue-500" : "text-neutral-500"}`} />
                <div className={`text-xs tracking-widest uppercase font-bold ${sub.is_pro ? "text-blue-500" : "text-neutral-500"}`}>{sub.is_pro ? "PRO" : "FREE"}</div>
              </div>
              <div className="font-display text-2xl uppercase mt-1">
                {sub.is_scholarship ? "Scholarship" : sub.is_pro ? (sub.in_trial ? "Trial Active" : "Pro Member") : "Free Plan"}
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                {sub.is_scholarship ? "Full access — gifted by admin"
                  : sub.is_pro ? `${sub.days_left} days remaining`
                  : "3 workouts · 1 buddy · 30-day history"}
              </div>
            </div>
            {!sub.is_pro && (
              <Link to="/pricing"><Button data-testid="upgrade-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9"><Crown className="w-3 h-3 mr-1" /> Upgrade</Button></Link>
            )}
            {sub.is_pro && !sub.is_scholarship && (
              <Link to="/pricing"><Button variant="outline" data-testid="manage-sub-btn" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-9">Manage</Button></Link>
            )}
          </div>
        </div>
      )}

      <form onSubmit={saveProfile} className="border border-neutral-800 p-5 mb-5 space-y-3">
        <h2 className="font-display text-xl uppercase">Edit Profile</h2>
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">Display name</Label>
          <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">My Gym (auto-tagged in shares)</Label>
          <Input data-testid="profile-gym-input" value={gym} onChange={(e) => setGym(e.target.value)} placeholder="e.g. Iron House Gym" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">Distance unit</Label>
          <div className="mt-2 flex gap-2">
            {["km", "mi"].map((u) => (
              <button key={u} type="button" data-testid={`unit-${u}`} onClick={() => setUnit(u)}
                className={`flex-1 py-2.5 border font-display uppercase tracking-wider text-sm transition-colors ${
                  unit === u ? "bg-blue-500 border-blue-500 text-white" : "bg-transparent border-neutral-700 text-neutral-300 hover:border-blue-500"
                }`}>{u === "km" ? "Kilometres (km)" : "Miles (mi)"}</button>
            ))}
          </div>
        </div>
        <Button data-testid="profile-save-btn" type="submit" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
          <Save className="w-4 h-4 mr-2" /> Save
        </Button>
      </form>

      <div className="border border-neutral-800 p-5 mb-5">
        <h2 className="font-display text-xl uppercase mb-3">Notifications</h2>
        <PushToggleRow />
      </div>

      <div className="border border-neutral-800 p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl uppercase">Get the App</h2>
          <Link to="/install" data-testid="install-help-link" className="text-xs uppercase tracking-widest text-blue-500 font-bold">Help →</Link>
        </div>
        <InstallPrompt mode="button" />
      </div>

      <FitbitConnect />

      <TrainerProfileWidget />

      <div className="border border-neutral-800 p-5 mb-5">
        <h2 className="font-display text-xl uppercase mb-1">Trainer / Doctor Share</h2>
        <p className="text-xs text-neutral-500 mb-3">Generate a read-only link to share your progress with a trainer or medical professional. Expires in 30 days.</p>
        {!sub?.is_pro ? (
          <ProLock feature="Trainer share links" testId="pro-lock-share" />
        ) : (
          <>
            <Button data-testid="create-share-btn" onClick={createShare} disabled={creating} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
              <Link2 className="w-4 h-4 mr-2" /> Generate share link
            </Button>
            {shares.length > 0 && (
              <div className="mt-4 divide-y divide-neutral-800 border border-neutral-800">
                {shares.map((s) => (
                  <div key={s.share_token} className="p-3 flex items-center gap-2">
                    <div className="flex-1 text-xs text-neutral-300 truncate font-mono-d">/share/{s.share_token}</div>
                    <Button size="icon" variant="ghost" onClick={() => copy(s.share_token)} data-testid={`copy-${s.share_token}`} className="text-neutral-400 hover:text-blue-500 h-7 w-7"><Copy className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => revoke(s.share_token)} data-testid={`revoke-${s.share_token}`} className="text-neutral-400 hover:text-blue-500 h-7 w-7"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {isAdmin && (
        <div data-testid="profile-quick-grant" className="border border-blue-500/40 bg-blue-500/5 p-4 mb-3">
          <div className="flex items-center gap-2 text-blue-500 text-xs tracking-[0.3em] uppercase font-bold mb-2">
            <Crown className="w-3 h-3" /> QUICK GRANT
          </div>
          <p className="text-xs text-neutral-300 mb-2">Paste an email — instant scholarship.</p>
          <ProfileQuickGrant />
        </div>
      )}

      {isAdmin && (
        <Link to="/admin" data-testid="admin-link" className="block border border-blue-500/40 hover:border-blue-500 p-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 flex items-center justify-center"><Shield className="w-5 h-5 text-white" /></div>
            <div className="flex-1">
              <div className="font-display text-lg uppercase">Admin Control Room</div>
              <div className="text-xs text-neutral-400">Challenges · TikTok · Photos · Scholarships · Invite links</div>
            </div>
            <span className="text-xs uppercase tracking-widest text-blue-500">Open →</span>
          </div>
        </Link>
      )}

      <div className="space-y-2">
        <button data-testid="share-app-btn" onClick={shareApp} className="w-full flex items-center justify-between p-4 border border-neutral-800 hover:border-blue-500 text-left">
          <div>
            <div className="font-display text-lg uppercase">Invite Friends</div>
            <div className="text-xs text-neutral-500">Share app link</div>
          </div>
          <Share2 className="w-5 h-5 text-blue-500" />
        </button>

        <button data-testid="logout-btn" onClick={doLogout} className="w-full flex items-center justify-between p-4 border border-neutral-800 hover:border-blue-500 text-left">
          <div>
            <div className="font-display text-lg uppercase">Logout</div>
            <div className="text-xs text-neutral-500">Exit the app</div>
          </div>
          <LogOut className="w-5 h-5 text-blue-500" />
        </button>
      </div>
    </div>
  );
}

function ProfileQuickGrant() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const grant = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/scholarship/quick-grant", { email: email.trim(), on: true });
      toast.success(`Pro granted to ${email.trim()}`);
      setEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex gap-2">
      <Input
        data-testid="profile-quick-grant-email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), grant())}
        placeholder="email@example.com"
        type="email"
        className="bg-neutral-900 border-neutral-800 text-white rounded-none h-10"
      />
      <Button
        data-testid="profile-quick-grant-btn"
        onClick={grant}
        disabled={busy || !email}
        className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 shrink-0"
      >
        {busy ? "…" : "Grant"}
      </Button>
    </div>
  );
}
